import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import {
  CloudDaemonLogsResponse,
  CloudDaemonStats,
  CloudDaemonStatus,
  CloudSyncStatus,
  CloudUploadResponse,
} from '../../../../domain/src/lib/cloud/cloud.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class CloudService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly cloudPath = '/api/cloud';

  private cloudUrl(): string {
    return this.cloudPath;
  }

  /** Misma resolución que Retención / Custodia / Bóveda; override opcional como antes. */
  private effectiveTenantId(override?: string): string {
    const trimmed = (override ?? '').trim();
    if (trimmed) return trimmed;
    return resolveTenantId(this.serverContext);
  }

  getSyncStatus(tenantId?: string): Observable<CloudSyncStatus> {
    const tid = this.effectiveTenantId(tenantId);
    return this.http.get<CloudSyncStatus>(`${this.cloudUrl()}/status`, {
      headers: tenantHeaders(tid),
      params: withTenantParam(tid),
    });
  }

  uploadFiles(filePaths: string[], tenantId?: string): Observable<CloudUploadResponse> {
    const tid = this.effectiveTenantId(tenantId);
    return this.http.post<CloudUploadResponse>(
      `${this.cloudUrl()}/upload`,
      { file_paths: filePaths },
      {
        headers: tenantHeaders(tid),
        params: withTenantParam(tid),
      },
    );
  }

  getDaemonStatus(tenantId?: string): Observable<CloudDaemonStatus> {
    const tid = this.effectiveTenantId(tenantId);
    return this.http.get<CloudDaemonStatus>(`${this.cloudUrl()}/daemon/status`, {
      headers: tenantHeaders(tid),
      params: withTenantParam(tid),
    });
  }

  startDaemon(tenantId?: string): Observable<unknown> {
    const tid = this.effectiveTenantId(tenantId);
    return this.http.post(
      `${this.cloudUrl()}/daemon/start`,
      {},
      {
        headers: tenantHeaders(tid),
        params: withTenantParam(tid),
      },
    );
  }

  stopDaemon(tenantId?: string): Observable<unknown> {
    const tid = this.effectiveTenantId(tenantId);
    return this.http.post(
      `${this.cloudUrl()}/daemon/stop`,
      {},
      {
        headers: tenantHeaders(tid),
        params: withTenantParam(tid),
      },
    );
  }

  getDaemonStats(tenantId?: string): Observable<CloudDaemonStats> {
    const tid = this.effectiveTenantId(tenantId);
    return this.http.get<CloudDaemonStats>(`${this.cloudUrl()}/daemon/stats`, {
      headers: tenantHeaders(tid),
      params: withTenantParam(tid),
    });
  }

  getDaemonLogs(lines = 50, tenantId?: string): Observable<CloudDaemonLogsResponse> {
    const tid = this.effectiveTenantId(tenantId);
    let params = withTenantParam(tid) ?? new HttpParams();
    params = params.set('lines', String(lines));

    return this.http.get<CloudDaemonLogsResponse>(`${this.cloudUrl()}/daemon/logs`, {
      headers: tenantHeaders(tid),
      params,
    });
  }
}
